import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { SwitchOption } from "./SwitchOption";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useApi } from "@/hooks/use-api";
import { useAppState } from "@/store/useApp";
import { AssessmentInfo } from "@/types/assessment";
import * as Sentry from "@sentry/react";
import { Info, Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

interface PublishAssessmentDialogProps {
  courseId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assessment?: AssessmentInfo;
  disableAssessmentSelect?: boolean;
  mode?: "publish" | "edit";
}

export const PublishAssessmentDialog = ({
  courseId,
  open,
  onOpenChange,
  assessment,
  disableAssessmentSelect,
  mode = "publish",
}: PublishAssessmentDialogProps) => {
  const [activeTab, setActiveTab] = useState<"details" | "portal">("details");
  const [selectedAssessmentId, setSelectedAssessmentId] = useState<string>("");
  const [scheduledFrom, setScheduledFrom] = useState<Date | undefined>();
  const [scheduledUntil, setScheduledUntil] = useState<Date | undefined>();
  const [isFormative, setIsFormative] = useState(false);
  const [shareFeedback, setShareFeedback] = useState(false);
  const [shareCriteria, setShareCriteria] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [shareAnnotations, setShareAnnotations] = useState(false);
  const [shareTranscript, setShareTranscript] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { patchAssessment } = useApi();
  const assessments = useAppState((state) =>
    state.assessments?.filter(
      (a) =>
        a.courseId === courseId &&
        (a.status === "pending" || a.id === assessment?.id)
    )
  );

  const selectedAssessment = useMemo(
    () => assessments?.find((item) => item.id === selectedAssessmentId),
    [assessments, selectedAssessmentId]
  );
  const currentAssessment = selectedAssessment ?? assessment;
  const isWrittenAssessment = currentAssessment?.type === "written";
  const hasAtLeastOneFeedbackComponent =
    shareFeedback ||
    shareCriteria ||
    showSuggestions ||
    shareAnnotations ||
    (!isWrittenAssessment && shareTranscript);

  const formatDateToLocalString = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const hydrateFromAssessment = (item?: AssessmentInfo) => {
    setScheduledFrom(item?.scheduledFrom ? new Date(item.scheduledFrom) : undefined);
    setScheduledUntil(item?.scheduledUntil ? new Date(item.scheduledUntil) : undefined);
    setIsFormative(!!item?.isFormative);
    setShareFeedback(!!item?.shareFeedback);
    setShareCriteria(!!item?.shareCriteria);
    setShowSuggestions(!!item?.showSuggestions);
    setShareAnnotations(!!item?.shareAnnotations);
    setShareTranscript(item?.type === "written" ? false : !!item?.shareTranscript);
  };

  useEffect(() => {
    if (!open) return;

    setActiveTab("details");
    setError(null);

    if (assessment) {
      setSelectedAssessmentId(assessment.id);
      hydrateFromAssessment(assessment);
      return;
    }

    setSelectedAssessmentId("");
    hydrateFromAssessment(undefined);
  }, [open, assessment]);

  useEffect(() => {
    if (!isWrittenAssessment) return;
    if (shareTranscript) {
      setShareTranscript(false);
    }
  }, [isWrittenAssessment, shareTranscript]);

  const handleAssessmentChange = (assessmentId: string) => {
    setSelectedAssessmentId(assessmentId);
    const item = assessments?.find((a) => a.id === assessmentId);
    hydrateFromAssessment(item);
  };

  const handleUnpublish = async () => {
    if (!selectedAssessmentId) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await patchAssessment(courseId, selectedAssessmentId, {
        status: "pending",
        scheduledFrom: null,
        scheduledUntil: null,
      });
      onOpenChange(false);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to unpublish assessment";
      Sentry.captureException(err);
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedAssessmentId || !hasAtLeastOneFeedbackComponent) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const payload: Partial<AssessmentInfo> = {
        scheduledFrom: scheduledFrom
          ? formatDateToLocalString(scheduledFrom)
          : mode === "publish"
            ? formatDateToLocalString(new Date())
            : null,
        scheduledUntil: scheduledUntil
          ? formatDateToLocalString(scheduledUntil)
          : null,
        isFormative,
        shareFeedback,
        shareCriteria,
        showSuggestions,
        shareAnnotations,
        shareTranscript: isWrittenAssessment ? false : shareTranscript,
      };

      if (mode === "publish" && selectedAssessment?.status === "pending") {
        payload.status = "active";
        payload.feedbackLive = isFormative;
      }

      await patchAssessment(courseId, selectedAssessmentId, payload);
      onOpenChange(false);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to save assessment";
      Sentry.captureException(err);
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const showUnpublish = !!selectedAssessment && selectedAssessment.status !== "pending";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[580px]">
        <DialogHeader>
          <DialogTitle>
            {mode === "edit" ? "Edit assessment" : "Publish assessment"}
          </DialogTitle>
          <DialogDescription>
            {mode === "edit"
              ? "Update assessment details and portal settings."
              : "Upload an assessment to the student portal."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="text-sm text-red-500 bg-red-50 p-2 rounded">
              {error}
            </div>
          )}

          <Tabs
            value={activeTab}
            onValueChange={(value) => setActiveTab(value as "details" | "portal")}
            className="w-full"
          >
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="portal">Portal</TabsTrigger>
            </TabsList>
          </Tabs>

          {activeTab === "details" && (
            <div className="space-y-4">
              <div className="grid w-full gap-1.5">
                <label className="text-sm font-medium leading-none">Assessment</label>
                <Select
                  value={selectedAssessmentId}
                  onValueChange={handleAssessmentChange}
                  disabled={disableAssessmentSelect || mode === "edit"}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select assessment..." />
                  </SelectTrigger>
                  <SelectContent>
                    {assessments?.map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium leading-none">Start date</label>
                  <DatePicker
                    date={scheduledFrom}
                    setDate={setScheduledFrom}
                    placeholder="Immediately"
                  />
                  <p className="text-sm text-muted-foreground">
                    The date all students can access the assessment.
                  </p>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium leading-none">Deadline</label>
                  <DatePicker
                    date={scheduledUntil}
                    setDate={setScheduledUntil}
                    placeholder="Pick a date"
                  />
                  <p className="text-sm text-muted-foreground">
                    The date all students are required to submit by.
                  </p>
                </div>
              </div>

              <Separator />

              <SwitchOption
                id="isFormative"
                label="Instant AI feedback"
                description="Students receive ungraded, AI-generated feedback via the portal."
                checked={isFormative}
                onCheckedChange={setIsFormative}
                labelClassName="text-sm"
              />
            </div>
          )}

          {activeTab === "portal" && (
            <div className="space-y-4">
              <Alert className="mb-4">
                <Info className="h-4 w-4" />
                <AlertTitle>Customize feedback reports</AlertTitle>
                <AlertDescription>
                  Tailor your feedback report to fit your assessment.
                </AlertDescription>
              </Alert>

              <SwitchOption
                id="shareFeedback"
                label="Written feedback"
                description="Summary of the strengths and weaknesses."
                checked={shareFeedback}
                onCheckedChange={setShareFeedback}
              />

              <SwitchOption
                id="shareCriteria"
                label="Grading breakdown"
                description="Grading remarks grouped by rubric criteria."
                checked={shareCriteria}
                onCheckedChange={setShareCriteria}
              />

              <SwitchOption
                id="showSuggestions"
                label="Reading Suggestions"
                description="Share reading suggestions"
                checked={showSuggestions}
                onCheckedChange={setShowSuggestions}
              />

              <SwitchOption
                id="shareAnnotations"
                label="Annotations"
                description="Includes original notes and their referenced blocks."
                checked={shareAnnotations}
                onCheckedChange={setShareAnnotations}
              />

              <SwitchOption
                id="shareTranscript"
                label="Transcript"
                description="Shows the transcript of the student's AI conversation."
                checked={shareTranscript}
                onCheckedChange={setShareTranscript}
                disabled={isWrittenAssessment}
              />
            </div>
          )}

          <DialogFooter className="flex items-center justify-between sm:justify-between">
            <div>
              {showUnpublish && (
                <Button
                  type="button"
                  variant="ghost"
                  className="text-red-500 hover:text-red-600"
                  onClick={handleUnpublish}
                  disabled={isSubmitting}
                >
                  Unpublish
                </Button>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  !selectedAssessmentId ||
                  isSubmitting ||
                  !hasAtLeastOneFeedbackComponent
                }
              >
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {mode === "edit" ? "Save changes" : "Publish assessment"}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
